<template>
  <div class="oauth-register-endpoint">
    <!-- This component handles POST /oauth/register for Dynamic Client Registration (RFC 7591) -->
    <!-- It's not rendered visually - it responds programmatically to API requests -->
  </div>
</template>

<script>
/**
 * Dynamic Client Registration Endpoint (RFC 7591)
 *
 * This endpoint is called by MCP Inspector before the OAuth flow starts
 * to register itself as an OAuth client.
 *
 * Request: POST /oauth/register
 * {
 *   "redirect_uris": ["http://localhost:6274/oauth/callback"],
 *   "grant_types": ["authorization_code"],
 *   "response_types": ["code"],
 *   "token_endpoint_auth_method": "none",
 *   "client_name": "MCP Inspector"
 * }
 *
 * Response: 201 Created
 * {
 *   "client_id": "mcp-inspector",
 *   "client_secret": "",
 *   "redirect_uris": [...echoed back...],
 *   "grant_types": ["authorization_code"],
 *   "response_types": ["code"],
 *   "token_endpoint_auth_method": "none"
 * }
 */

export default {
  name: 'OAuthRegister',
  data() {
    return {
      registrationData: null,
      responseData: null,
      error: null,
    }
  },
  mounted() {
    // Handle the client registration request
    this.handleClientRegistration()
  },
  methods: {
    handleClientRegistration() {
      try {
        // Get registration data from query params (for browser-based testing)
        // In a real implementation, this would come from POST body
        const query = this.$route.query

        // Check if there's pending registration data in sessionStorage
        const pendingRegistration = sessionStorage.getItem('oauth_client_registration')
        let registrationRequest = null

        if (pendingRegistration) {
          // Data was passed via sessionStorage (POST-like behavior)
          sessionStorage.removeItem('oauth_client_registration')
          registrationRequest = JSON.parse(pendingRegistration)
        } else if (query.redirect_uris) {
          // Data passed via query params (GET fallback for testing)
          registrationRequest = {
            redirect_uris: Array.isArray(query.redirect_uris)
              ? query.redirect_uris
              : [query.redirect_uris],
            grant_types: query.grant_types ? [query.grant_types] : ['authorization_code'],
            response_types: query.response_types ? [query.response_types] : ['code'],
            token_endpoint_auth_method: query.token_endpoint_auth_method || 'none',
            client_name: query.client_name || 'MCP Inspector',
          }
        } else {
          // No registration data provided
          this.sendErrorResponse(400, 'invalid_request', 'Missing registration parameters')
          return
        }

        this.registrationData = registrationRequest

        // Validate required fields
        if (!registrationRequest.redirect_uris || registrationRequest.redirect_uris.length === 0) {
          this.sendErrorResponse(400, 'invalid_redirect_uri', 'redirect_uris is required')
          return
        }

        // Create the registration response
        const response = {
          client_id: 'mcp-inspector', // Static for browser cache implementation
          client_secret: '', // Empty - using PKCE instead
          redirect_uris: registrationRequest.redirect_uris, // Echo back
          grant_types: registrationRequest.grant_types || ['authorization_code'],
          response_types: registrationRequest.response_types || ['code'],
          token_endpoint_auth_method: registrationRequest.token_endpoint_auth_method || 'none',
        }

        // Store the registration (browser cache implementation)
        this.storeClientRegistration(response)

        // Send successful response
        this.sendSuccessResponse(response)
      } catch (error) {
        console.error('Client registration error:', error)
        this.sendErrorResponse(500, 'server_error', 'An error occurred during client registration')
      }
    },

    storeClientRegistration(registration) {
      // Store in localStorage for later validation
      // In production, this would be stored in a database
      try {
        const clients = JSON.parse(localStorage.getItem('oauth_clients') || '{}')
        clients[registration.client_id] = {
          ...registration,
          registered_at: Date.now(),
        }
        localStorage.setItem('oauth_clients', JSON.stringify(clients))
        console.log('Client registered:', registration.client_id)
      } catch (error) {
        console.error('Error storing client registration:', error)
      }
    },

    sendSuccessResponse(data) {
      this.responseData = data

      // Store response in sessionStorage for retrieval
      sessionStorage.setItem('oauth_register_response', JSON.stringify({
        success: true,
        status: 201,
        data,
      }))

      console.log('Client registration successful:', data)

      // Display success notification
      this.$vs.notify({
        title: 'Client Registered',
        text: `Client "${data.client_id}" registered successfully`,
        iconPack: 'feather',
        icon: 'icon-check',
        color: 'success',
      })
    },

    sendErrorResponse(status, error, errorDescription) {
      this.error = {
        status,
        error,
        error_description: errorDescription,
      }

      // Store error in sessionStorage
      sessionStorage.setItem('oauth_register_response', JSON.stringify({
        success: false,
        error: this.error,
      }))

      console.error('Client registration error:', this.error)

      // Display error notification
      this.$vs.notify({
        title: 'Registration Failed',
        text: errorDescription,
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
      })
    },
  },
}
</script>

<style scoped>
.oauth-register-endpoint {
  display: none;
}
</style>
